// src/pages/DietCalendar.js — 월 달력 + 날짜 상세(오른쪽 패널). /DietCalendar 와 /diet/:date 공용
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getDietHistCalendar, getUserInfo } from '../api/dietApi';
import Drawer from '../components/ui/Drawer';
import Modal from '../components/ui/Modal';
import DayEditor from './DayEditor';
import './DietCalendar.css';

const WEEK = ['일', '월', '화', '수', '목', '금', '토'];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export default function DietCalendar() {
  const navigate = useNavigate();
  const { date } = useParams();
  const [searchParams] = useSearchParams();
  const today = new Date();
  const todayStr = ymd(today.getFullYear(), today.getMonth(), today.getDate());
  const openDate = date && DATE_RE.test(date) ? date : null;

  // 보여줄 달: 열린 날짜 > 쿼리(year, month) > 오늘
  const [view, setView] = useState(() => {
    if (openDate) return { y: Number(openDate.slice(0, 4)), m: Number(openDate.slice(5, 7)) - 1 };
    const y = parseInt(searchParams.get('year'), 10);
    const m = parseInt(searchParams.get('month'), 10);
    return Number.isNaN(y) || Number.isNaN(m) ? { y: today.getFullYear(), m: today.getMonth() } : { y, m: m - 1 };
  });
  const [kcalMap, setKcalMap] = useState({});
  const [goal, setGoal] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    getUserInfo().then((r) => setGoal(Number(r.data?.wantKcal) || 0)).catch(() => {});
  }, []);

  const loadMonth = useCallback(() => {
    getDietHistCalendar(view.y, view.m + 1)
      .then((res) => {
        const map = {};
        (Array.isArray(res.data) ? res.data : []).forEach((it) => {
          map[ymd(view.y, view.m, it.histDay)] = Math.round(Number(it.dayKcal) || 0);
        });
        setKcalMap(map);
      })
      .catch(() => setKcalMap({}));
  }, [view]);

  useEffect(() => { loadMonth(); }, [loadMonth]);

  // 다른 달 날짜로 바로 들어오면 그 달로 이동
  useEffect(() => {
    if (!openDate) return;
    const y = Number(openDate.slice(0, 4)); const m = Number(openDate.slice(5, 7)) - 1;
    setView((v) => (v.y === y && v.m === m ? v : { y, m }));
  }, [openDate]);

  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1).getDay();
    const days = new Date(view.y, view.m + 1, 0).getDate();
    return [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  }, [view]);

  const move = (delta) => setView(({ y, m }) => {
    const d = new Date(y, m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  const openDay = (key) => navigate(`/diet/${key}`);
  const doClose = () => {
    setConfirmClose(false);
    setDirty(false);
    navigate(`/DietCalendar?year=${view.y}&month=${view.m + 1}`);
  };
  const requestClose = () => (dirty ? setConfirmClose(true) : doClose());

  const drawerTitle = openDate
    ? `${Number(openDate.slice(5, 7))}월 ${Number(openDate.slice(8, 10))}일 (${WEEK[new Date(openDate).getDay()]})`
    : '';

  return (
    <div className="cal">
      <div className="cal-head">
        <h2>{view.y}년 {view.m + 1}월</h2>
        <div className="cal-nav">
          <button className="btn ghost" type="button" onClick={() => move(-1)} aria-label="이전 달">‹</button>
          <button className="btn" type="button" onClick={() => setView({ y: today.getFullYear(), m: today.getMonth() })}>오늘</button>
          <button className="btn ghost" type="button" onClick={() => move(1)} aria-label="다음 달">›</button>
        </div>
      </div>

      <div className="cal-grid">
        {WEEK.map((w, i) => <div key={w} className={`cal-w${i === 0 ? ' sun' : ''}${i === 6 ? ' sat' : ''}`}>{w}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`b${i}`} className="cal-cell blank" />;
          const key = ymd(view.y, view.m, d);
          const kcal = kcalMap[key] || 0;
          const ratio = goal > 0 ? Math.min(kcal / goal, 1) : 0;
          const cls = ['cal-cell', key === todayStr && 'today', key === openDate && 'open', kcal > 0 && 'has'].filter(Boolean).join(' ');
          return (
            <button key={key} type="button" className={cls} onClick={() => openDay(key)}>
              <span className={`cal-d${i % 7 === 0 ? ' sun' : ''}${i % 7 === 6 ? ' sat' : ''}`}>{d}</span>
              {kcal > 0 && (
                <>
                  <span className="cal-kcal">{kcal.toLocaleString('ko-KR')} kcal</span>
                  {goal > 0 && <span className={`cal-bar${kcal > goal ? ' over' : ''}`}><span style={{ width: `${ratio * 100}%` }} /></span>}
                </>
              )}
            </button>
          );
        })}
      </div>

      <Drawer open={!!openDate} title={drawerTitle} onClose={requestClose}>
        {openDate && (
          <DayEditor
            key={openDate}
            date={openDate}
            onDirtyChange={setDirty}
            onSaved={loadMonth}
          />
        )}
      </Drawer>

      <Modal
        open={confirmClose}
        title="저장하지 않은 변경"
        size="sm"
        onClose={() => setConfirmClose(false)}
        footer={(
          <>
            <button className="btn ghost" type="button" onClick={() => setConfirmClose(false)}>계속 편집</button>
            <button className="btn danger" type="button" onClick={doClose}>저장하지 않고 닫기</button>
          </>
        )}
      >
        <p className="confirm-text">저장하지 않은 변경이 있어요. 닫으면 변경 내용이 사라집니다.</p>
      </Modal>
    </div>
  );
}
